import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngrx/store';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { loadUserProfileSuccess, loadUserProfileFailure } from '@/store/current-user/profile.actions';
import { CurrentUserStore } from '@/store/current-user/current-user.store';
import { TokenMonitorService } from '../token-monitor.service';
import { LoggerService } from '@/services/logger.service';
import { ServiceHealthService } from '@/core/services/service-health.service';

@Injectable({
  providedIn: 'root'
})
export class AppInitializerService {
  private oidcSecurityService = inject(OidcSecurityService);
  private store = inject(Store);
  private http = inject(HttpClient);
  private tokenMonitorService = inject(TokenMonitorService);
  private apiUrl = `${window.location.origin}/api`;
  private currentUserStore = inject(CurrentUserStore);
  private logger = inject(LoggerService);
  private serviceHealth = inject(ServiceHealthService);

  async initializeApp(): Promise<void> {
    this.logger.log('AppInitializer', '🚀', 'Starting at', new Date().toISOString());

    try {
      // Check if we're on special routes - if so, skip initialization
      // to let those components handle their respective flows
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      const isCallbackRoute = currentUrl.startsWith('/auth/callback');
      const isLogoutRoute = currentUrl.startsWith('/auth/logout');
      const isServiceUnavailableRoute = currentUrl.startsWith('/service-unavailable');

      if (isCallbackRoute) {
        this.logger.log('AppInitializer', '⏭️', 'On callback route, skipping initialization');
        return;
      }

      if (isLogoutRoute) {
        this.logger.log('AppInitializer', '⏭️', 'On logout route, skipping initialization');
        return;
      }

      if (isServiceUnavailableRoute) {
        this.logger.log('AppInitializer', '⏭️', 'On service-unavailable route, skipping initialization');
        return;
      }

      // Store the current URL if not authenticated
      if (currentUrl !== '/' && !sessionStorage.getItem('redirectUrl')) {
        this.logger.log('AppInitializer', '📌', 'Storing initial URL:', currentUrl);
        sessionStorage.setItem('redirectUrl', currentUrl);
      }

      // First ensure OIDC is configured
      this.logger.log('AppInitializer', '🔧', 'Checking OIDC configuration...');
      const configResult = await firstValueFrom(this.oidcSecurityService.getConfiguration());
      this.logger.log('AppInitializer', '🔧', 'OIDC configured:', !!configResult);

      // Check authentication status
      this.logger.log('AppInitializer', '🔐', 'Checking authentication status...');
      const authResult = await firstValueFrom(this.oidcSecurityService.checkAuth());
      this.logger.log('AppInitializer', '🔐', 'Auth result:', authResult);

      if (authResult) {
        this.logger.success('AppInitializer', 'User is authenticated, fetching ID token...');

        // Get the ID token
        const idToken = await firstValueFrom(this.oidcSecurityService.getIdToken());
        this.logger.log('AppInitializer', '🔑', 'Got ID token:', idToken ? 'Yes' : 'No');

        if (idToken) {
          // Load user profile with roles from /me endpoint
          const meEndpoint = `${this.apiUrl}/users/me`;
          this.logger.log('AppInitializer', '📡', 'Calling endpoint:', meEndpoint);

          try {
            const userProfile = await firstValueFrom(
              this.http.get<any>(meEndpoint, {
                headers: {
                  Authorization: `Bearer ${idToken}`,
                },
              })
            );

            this.logger.log('AppInitializer', '✨', 'User profile loaded successfully:', {
              id: userProfile?.id,
              email: userProfile?.email,
              roles: userProfile?.roles,
              hasRoles: !!userProfile?.roles,
              roleCount: userProfile?.roles?.length || 0
            });

            // Dispatch success action with user profile including roles
            this.store.dispatch(loadUserProfileSuccess({ userProfile }));

            // Set user in the new signal store
            this.currentUserStore.setUser(userProfile);

            // Load user teams immediately after user profile
            this.logger.log('AppInitializer', '👥', 'Loading user teams...');
            await this.currentUserStore.loadUserTeams();
            this.logger.success('AppInitializer', 'User teams loaded');

            // Start monitoring token expiration for already-authenticated users
            this.tokenMonitorService.startMonitoring();
            this.logger.log('AppInitializer', '⏰', 'Token monitoring started');
          } catch (error: any) {
            this.logger.error('AppInitializer', 'Failed to load user profile', {
              error: error.message,
              status: error.status,
              url: meEndpoint,
              fullError: error
            });
            this.store.dispatch(loadUserProfileFailure({ error: 'Failed to load user profile' }));

            // Handle 5xx errors - check if service is completely down
            if (error instanceof HttpErrorResponse && error.status >= 500) {
              this.serviceHealth.addFailure('/api/users/me', error.status);
              this.serviceHealth.setLastError(error);

              // Check liveness to determine if we should redirect
              const isLive = await this.serviceHealth.checkLiveness();
              if (!isLive) {
                this.logger.error('AppInitializer', 'Service is completely unavailable, redirecting to error page');
                window.location.href = '/service-unavailable';
                return;
              }
            }
            // Don't throw error here - allow app to continue but without user profile
          }
        } else {
          this.logger.warn('AppInitializer', 'No ID token available');
          this.store.dispatch(loadUserProfileFailure({ error: 'No ID token available' }));
        }
      } else {
        this.logger.log('AppInitializer', '🔓', 'User is not authenticated');
        // Settle the profile load so role guards do not wait on it.
        this.store.dispatch(loadUserProfileFailure({ error: 'Not authenticated' }));
      }

      this.logger.success('AppInitializer', 'Initialization complete at ' + new Date().toISOString());
    } catch (error) {
      this.logger.error('AppInitializer', 'Error during initialization', error);
      this.store.dispatch(loadUserProfileFailure({ error: 'Initialization failed' }));
      // Don't throw - allow app to continue loading
    }
  }
}

export function initializeAppFactory(appInitService: AppInitializerService) {
  return () => appInitService.initializeApp();
}